import { Drawer, Stack, Group, TextInput, ActionIcon } from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconUser, IconAt, IconX, IconPlus } from "@tabler/icons-react"
import { Button } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { z } from "zod"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { standardEmail } from "@tfg/types/schemas/utils"

import { useInviteUsersMutation } from "./hooks"

const inviteUsersSchema = z.object({
  users: z.array(
    z.object({
      name: z.string().min(1),
      email: standardEmail(),
    })
  ),
})

type InviteUsersForm = z.infer<typeof inviteUsersSchema>

interface Props {
  opened: boolean
  organization: AdminResponses.Organization
  onClose(): void
}

export const InviteUsersDrawer = ({ opened, organization, onClose }: Props) => {
  const { mutate: inviteUsers, isPending: isInviting } = useInviteUsersMutation(
    organization.id.toString(),
    {
      onSuccess: onClose,
    }
  )

  const form = useForm<InviteUsersForm>({
    validate: zod4Resolver(inviteUsersSchema),
    initialValues: {
      users: [
        {
          name: "",
          email: "",
        },
      ],
    },
  })

  const handleInviteUsers = (values: InviteUsersForm) => {
    inviteUsers(values)
  }

  return (
    <Drawer
      position="right"
      size="lg"
      opened={opened}
      onClose={onClose}
      title="Create Users"
    >
      <form onSubmit={form.onSubmit(handleInviteUsers)}>
        <Stack gap="sm">
          {form.values.users.map((_, index) => (
            <Stack key={index}>
              <Group w="100%">
                <TextInput
                  placeholder="User name"
                  label="Name"
                  leftSection={<IconUser size={16} />}
                  {...form.getInputProps(`users.${index}.name`)}
                />
                <TextInput
                  label="Email"
                  placeholder="Email address"
                  leftSection={<IconAt size={16} />}
                  {...form.getInputProps(`users.${index}.email`)}
                />
                <ActionIcon
                  mt={26}
                  onClick={() => form.removeListItem("users", index)}
                >
                  <IconX />
                </ActionIcon>
              </Group>
            </Stack>
          ))}
          <Group justify="apart">
            <Button
              onClick={() =>
                form.insertListItem("users", { name: "", email: "" })
              }
              icon={<IconPlus size={16} />}
              variant="tertiary"
            >
              Add one
            </Button>
            <Button loading={isInviting} type="submit">
              Create users
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  )
}
